import { isAncestorOf, isTemplateProperty } from "@animech/axiom-utility";
import { assert } from "@animech/core-utilities";
import { z } from "zod";
import { ProductId } from "@animech/axiom-format/src/definitions/definition-bindings";
import { Configurator, ConfiguratorChange } from "./definitions-utility";
import {
	isPreferenceValue,
	PreferenceMapping,
	Preference,
	PreferenceValue,
	PrimitivePreference,
	PropertyPreference,
	propertyBelongsToPropertyPreference,
	PreferenceValueSchema,
} from "./preference";
import { Property } from "src/common";

const PreferencesDataSchema = z.object({
	preferences: z.record(
		z.string(),
		z.record(z.string(), PreferenceValueSchema.optional()),
	),
});

type PreferencesData = z.infer<typeof PreferencesDataSchema>;

export type PreferencesChangeSet = PreferencesData["preferences"];

export class PreferencesManager {
	private _data: PreferencesData;
	private _configurator: Configurator;
	private _preferencesByName: Map<string, Preference[]>;
	private _nameByPreference: Map<Preference, string>;
	private _getPropertyState: Configurator["get"];
	private _getInStockDomainForProduct: (productId: ProductId) => unknown[];

	constructor(
		configurator: Configurator,
		getInStockDomainForProduct: (productId: ProductId) => unknown[],
		preferences?: PreferenceMapping,
	) {
		this._data = {
			preferences: {},
		};
		this._configurator = configurator;
		this._preferencesByName = new Map();
		this._nameByPreference = new Map();
		this._getPropertyState = configurator.get.bind(configurator);
		this._getInStockDomainForProduct = getInStockDomainForProduct;

		if (preferences) {
			this.registerPreferences(preferences);
		}
	}

	get data(): PreferencesData {
		return this._data;
	}

	registerPreferences(mapping: PreferenceMapping): void {
		for (const [name, preferenceOrPreferences] of Object.entries(mapping)) {
			assert(
				!this._preferencesByName.has(name),
				`The preference name ${name} has already been registered`,
			);
			const preferences = Array.isArray(preferenceOrPreferences)
				? preferenceOrPreferences
				: [preferenceOrPreferences];

			for (const preference of preferences) {
				const existingName = this._nameByPreference.get(preference);
				assert(
					!existingName || existingName === name,
					`The preference with name ${name} has already been mapped to ${existingName}`,
				);
				this._nameByPreference.set(preference, name);
			}
			this._preferencesByName.set(name, preferences);
		}
	}

	getValue<TValue extends PreferenceValue>(
		preference: PrimitivePreference<string, TValue>,
	): TValue | undefined {
		const name = this._nameByPreference.get(preference);
		assert(name, "Preference is not mapped to a name");
		const prefsData = this._data.preferences;
		const preferenceValue = prefsData[name]?.[preference.preferenceKey];
		return preference.validateValue(preferenceValue)
			? preferenceValue
			: undefined;
	}

	setValue<TValue extends PreferenceValue>(
		preference: PrimitivePreference<string, TValue>,
		value: TValue,
	): void {
		if (!preference.validateValue(value)) {
			return;
		}
		const name = this._nameByPreference.get(preference);
		assert(name, "Preference is not mapped to a name");
		const prefsData = this._data.preferences;
		prefsData[name] ??= {};
		prefsData[name][preference.preferenceKey] = value;
	}

	updatePreferencesFromProperties(
		preferences: PropertyPreference[],
		properties: Property[],
	): PreferencesChangeSet {
		const get = this._getPropertyState;
		const prefsData = this._data.preferences;
		const changeSet: PreferencesChangeSet = {};

		for (const property of properties) {
			const propertyPath = property.getPath();
			if (propertyPath.includes("$key")) {
				continue;
			}
			if (isTemplateProperty(property)) {
				throw new Error("Template properties are not allowed");
			}

			for (const preference of preferences) {
				const preferenceName = this._nameByPreference.get(preference);
				assert(preferenceName, "Preference is not mapped to a name");

				if (!propertyBelongsToPropertyPreference(property, preference)) {
					continue;
				}
				const propertyValue = this._configurator.get(property).value;
				if (propertyValue !== undefined && !isPreferenceValue(propertyValue)) {
					throw new Error(
						`Invalid type for property preference value: ${propertyValue}!`,
					);
				}

				const templateIndex = this._resolveTemplateIndex(
					preference.template,
					property,
				);
				const preferenceKey = preference.preferenceKey(propertyValue, {
					get,
					property,
					templateIndex,
				});
				if (!preferenceKey) {
					continue;
				}
				for (const key of [
					preferenceKey.key,
					...(preferenceKey.compatibleKeys ?? []),
				]) {
					const preferenceValue = prefsData[preferenceName]?.[key];
					if (preferenceValue !== propertyValue) {
						if (propertyValue !== undefined) {
							prefsData[preferenceName] ??= {};
							prefsData[preferenceName][key] = propertyValue;
							changeSet[preferenceName] ??= {};
							changeSet[preferenceName][key] = propertyValue;
						} else {
							if (prefsData[preferenceName]?.[key]) {
								delete prefsData[preferenceName][key];
							}
							if (changeSet[preferenceName]?.[key]) {
								delete changeSet[preferenceName][key];
							}
						}
					}
				}
			}
		}

		return changeSet;
	}

	applyPreferencesToProperties(
		preferences: PropertyPreference[],
		properties: Property[],
	): ConfiguratorChange[] {
		const get = this._getPropertyState;
		const prefsData = this._data.preferences;
		const changes: ConfiguratorChange[] = [];

		for (const property of properties) {
			const propertyPath = property.getPath();
			if (propertyPath.includes("$key")) {
				continue;
			}
			if (isTemplateProperty(property)) {
				throw new Error("Template properties are not allowed");
			}

			const propertyValue = this._configurator.get(property).value;

			for (const preference of preferences) {
				const preferenceName = this._nameByPreference.get(preference);
				assert(preferenceName, "Preference is not mapped to a name");

				if (!propertyBelongsToPropertyPreference(property, preference)) {
					continue;
				}

				const templateIndex = this._resolveTemplateIndex(
					preference.template,
					property,
				);
				const key = preference.preferenceKey(propertyValue, {
					get,
					property,
					templateIndex,
				})?.key;
				if (!key) {
					continue;
				}
				const hasPreferenceValue =
					!!prefsData[preferenceName] && key in prefsData[preferenceName];
				if (!hasPreferenceValue) {
					continue;
				}

				const preferenceValue = prefsData[preferenceName]![key];

				if (
					this._getInStockDomainForProduct(key as ProductId).includes(
						preferenceValue,
					)
				) {
					changes.push(this._configurator.set(property, preferenceValue));
				}
			}
		}

		return changes;
	}

	private _resolveTemplateIndex(
		template: Property | undefined,
		instance: Property,
	): number {
		if (!template) {
			return NaN;
		}
		assert(
			isAncestorOf(template, instance),
			`The property is not a descendant of the template`,
		);
		const indexPathPart = instance.getPath().at(template.getPath().length);
		return Number(indexPathPart);
	}
}
